import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import { IconType } from 'react-icons';
import { Link } from 'react-router-dom';
import '../../../src/AssetStatusForm.scss'
import '../common/Table/table-sort-icon.scss';

type SetPageFunction = React.Dispatch<React.SetStateAction<number>>;

interface TableProps {
    data: any[];
    columns: any[];
    defaultSortColumn: string;
    defaultSortOrder: string;
    pageSize: number;
    columnHeaders: any[];
    page?: number;
    handlePageNumberChange?: (pageNumber: number) => void;
    pagniation?: boolean;
    assetId?: string;
}



export const AssetTableMobile: React.FC<TableProps> = ({ data, columns, defaultSortColumn, defaultSortOrder, pageSize, columnHeaders, pagniation, handlePageNumberChange, page, assetId }) => {

    const [sortColumn, setSortColumn] = useState(defaultSortColumn);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);

    // Sorting logic
    const sortedData = data.sort((a, b) => {
        const sortKey = sortColumn;
        const aValue = a[sortKey];
        const bValue = b[sortKey];

        // Check if both values are numbers
        const isANumber = !isNaN(parseFloat(aValue)) && isFinite(aValue);
        const isBNumber = !isNaN(parseFloat(bValue)) && isFinite(bValue);

        if (isANumber && isBNumber) {
            // Both values are numbers
            if (sortOrder === 'asc') {
                return parseFloat(aValue) - parseFloat(bValue);
            } else {
                return parseFloat(bValue) - parseFloat(aValue);
            }
        } else {
            // At least one of the values is not a number, perform string comparison
            if (sortOrder === 'asc') {
                return String(aValue).localeCompare(String(bValue));
            } else {
                return String(bValue).localeCompare(String(aValue));
            }
        }
    });

    const currentPage = page || 1;

    // Pagination logic
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData.slice(startIndex, endIndex);

    // Function to handle column click for sorting
    const handleColumnClick = (column: any) => {
        if (column === sortColumn) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    };


    const handlePageClick = (page: number) => {
        handlePageNumberChange && handlePageNumberChange(page);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1)
            handlePageNumberChange && handlePageNumberChange(currentPage - 1);
    };

    const handleNextPage = () => {
        const totalPages = Math.ceil(data.length / pageSize);
        if (currentPage < totalPages)
            handlePageNumberChange && handlePageNumberChange(currentPage + 1);
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(data.length / pageSize);

        const maxVisiblePages = 5; // Adjust this value based on your preference
        const startPage = Math.max(currentPage - Math.floor(maxVisiblePages / 2), 1);
        const endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);

        return (
            <div className='flex justify-between items-center mt-4'>
                <button
                    className={`flex ml-[100px] items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] ${currentPage === 1
                        ? "bg-darkcolor-300 cursor-not-allowed"
                        : "bg-transparent text-white"
                        }`}
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className='mr-2' />
                    Previous
                </button>

                <div className='flex'>
                    {Array.from({ length: totalPages }, (_, index) => index + 1)
                        .slice(startPage - 1, endPage)
                        .map((page) => (
                            <button
                                key={page}
                                className={`mx-1 w-[40px] h-[40px] text-neutral-25 ${currentPage === page ? "rounded-full bg-darkcolor-500" : ""
                                    }`}
                                onClick={() => handlePageClick(page)}
                            >
                                {page}
                            </button>
                        ))}

                    {endPage < totalPages && (
                        <span className='mx-1 text-neutral-25'>...</span>
                    )}
                </div>

                <button
                    className={`flex mr-[100px] items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] ${currentPage === totalPages
                        ? "bg-darkcolor-300 cursor-not-allowed"
                        : "bg-transparent text-white"
                        }`}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    Next
                    {<FontAwesomeIcon icon={faArrowRight} className='ml-2' />}
                </button>
            </div>
        );
    };
    const renderSortIcon = (column: string) => {
        const isActive = sortColumn === column;
        return (
            <FontAwesomeIcon
                icon={faSort}
                className={`table-sort-icon ml-1 ${isActive && sortOrder === "asc" ? "table-sort-icon--asc" : ""}`}
            />
        );
    };

    return (
        <div className="mx-auto w-full">
            <div className="bg-gray-600">
                <div className="mt-8 flow-root">
                    <div className="overflow-x-auto border border-darkcolor-600 rounded-lg">
                        <div style={{ maxHeight: "790px", overflowY: "auto" }}>
                            <table className="min-w-full divide-y divide-darkcolor-400">
                                <thead className="sticky top-[-4px] bg-darkcolor-600">
                                    <tr>
                                        {columns.map((column, index) => (
                                            <th
                                                scope="col"
                                                key={column}
                                                onClick={() => handleColumnClick(column)}
                                                className={`py-3.5 px-3 text-left text-sm font-semibold text-white header-compact-column vertical-header border-r border-darkcolor-400 outline outline-[0.5px] outline-darkcolor-400 ${index % 2 === 1 ? 'bg-darkcolor-500' : ''
                                                    }`}
                                                style={["arrivalDuration", "arrivalVelocity"].includes(column) ? { fontWeight: "bold", fontSize: "1.05rem" } : {}}
                                            >
                                                <label style={{ marginTop: "4px", marginBottom: "4px" }}>{renderSortIcon(column)}</label>
                                                {columnHeaders[index]}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-darkcolor-400 bg-darkcolor-600">
                                    {paginatedData.map((item, index) => {
                                        const nextRowFirstColumnValue = paginatedData[index - 1]
                                            ? paginatedData[index - 1][columns[1]]
                                            : new Date();
                                        return (
                                            <tr key={index} className="group">
                                                {columns.map((column, colIndex) => (
                                                    <td
                                                        key={column}
                                                        className={`whitespace-nowrap px-3 py-4 text-sm text-gray-300 compact-column border-r border-darkcolor-400 
                                                            group-hover:bg-darkcolor-400 
                                                            ${colIndex % 2 === 1 ? 'bg-darkcolor-500 group-hover:bg-darkcolor-400' : ''}
                                                        `}
                                                    >
                                                        <Link
                                                            to={assetId ? `/assetdetails?assetId=${encodeURIComponent(assetId)}` : "/assetdetails"}
                                                            state={{
                                                                currentTab: 2,
                                                                startTime: item['date'],
                                                                endTime: nextRowFirstColumnValue,
                                                                assetId: assetId,
                                                            }}
                                                            style={{ color: "inherit", textDecoration: "none", display: "block" }}
                                                        >
                                                            {["arrivalDuration", "arrivalVelocity"].includes(column) ? (
                                                                <h4 style={{ fontWeight: "bold", fontSize: "1.05rem" }}>{item[column]}</h4>
                                                            ) : (
                                                                item[column]
                                                            )}
                                                        </Link>
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* <div style={{ marginTop: "10px" }}>
                                <span className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">Page {currentPage}</span>
                                <button className="whitespace-nowrap px-3 py-4 text-sm text-gray-300" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                    Previous
                                </button>
                                <button className="whitespace-nowrap px-3 py-4 text-sm text-gray-300" onClick={() => handlePageChange(currentPage + 1)} disabled={endIndex >= sortedData.length}>
                                    Next
                                </button>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
            {/* {pagination && renderPagination()} */}
        </div>
    );
};